/**
 * Analytics helper — fires Google Analytics 4 + Google Ads events.
 * Only sends data on production (viratastro.cloud) — preview/dev are silent
 * because of the Production Analytics Guard in public/index.html.
 *
 * Conversion events covered:
 *   - consultation_cta_click   (every "Book / Consult Now" button)
 *   - whatsapp_click           (every WhatsApp button on the site)
 *   - call_click               (every tel: button)
 *   - generate_lead            (Contact + Consultation form submits)
 *   - begin_checkout           (Razorpay open)
 *   - purchase                 (Razorpay handler success)
 *   - payment_failed           (Razorpay payment failed)
 *
 * Also: persists UTM parameters in localStorage on first visit and attaches
 * them to every conversion event so we can attribute leads to ad source.
 */

const isProd = () => typeof window !== 'undefined' && window.__IS_PRODUCTION__ === true;

// Google Ads conversion ID (matches public/index.html)
const GOOGLE_ADS_ID = 'AW-973039407';
const UTM_STORAGE_KEY = 'va_utm';
const UTM_MAX_AGE_DAYS = 30;

// ============== UTM Capture & Attribution ==============

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'];

export const captureUTM = () => {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) found[k] = v.slice(0, 120);
    });
    if (Object.keys(found).length === 0) return;
    found._ts = Date.now();
    found._landing = window.location.pathname.slice(0, 120);
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(found));
  } catch (_) { /* never break the app */ }
};

export const getStoredUTM = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw);
    const ageMs = Date.now() - (data._ts || 0);
    if (ageMs > UTM_MAX_AGE_DAYS * 86400_000) {
      localStorage.removeItem(UTM_STORAGE_KEY);
      return {};
    }
    const { _ts, _landing, ...utm } = data;
    return utm;
  } catch (_) { return {}; }
};

// ============== Core event firing ==============

export const trackEvent = (eventName, params = {}) => {
  try {
    if (!isProd()) return;
    if (typeof window.gtag === 'function') {
      // attach UTMs to every event so GA4 reports show source attribution
      const utm = getStoredUTM();
      window.gtag('event', eventName, { ...utm, ...params });
    }
  } catch (_) { /* never break the app if analytics fails */ }
};

// ============== High-Level Conversion Funnels ==============

export const trackConsultationCTAClick = (source, extra = {}) => {
  trackEvent('consultation_cta_click', {
    cta_source: source,
    consultation_value_inr: 5100,
    currency: 'INR',
    ...extra,
  });
};

export const trackPoochoGurujiCTAClick = (source, extra = {}) => {
  // Fired when a user clicks the Poocho Guruji button embedded in a blog CTA.
  // Feb 07, 2026 — user-mandated event name for the blog-CTA funnel.
  trackEvent('cta_poocho_guruji_click', {
    source: source || 'blog_cta',
    ...extra,
  });
};

export const trackWhatsAppClick = (source, extra = {}) => {
  trackEvent('whatsapp_click', { cta_source: source, ...extra });
  // Also fire under Acharya Guruji's preferred funnel name "whatsapp_clicked"
  trackEvent('whatsapp_clicked', { cta_source: source, ...extra });
};

export const trackCallClick = (source, extra = {}) => {
  trackEvent('call_click', { cta_source: source, ...extra });
};

export const trackRashifalView = (rashi_name) => {
  trackEvent('rashifal_view', { rashi: rashi_name });
};

export const trackKundliGenerate = (extra = {}) => {
  trackEvent('kundli_generate', extra);
};

export const trackKundliPDFEmail = (extra = {}) => {
  trackEvent('kundli_pdf_email_request', { ...extra, value_lead: 1 });
};

export const trackBlogRead = (slug) => {
  trackEvent('blog_view', { blog_slug: slug });
};

export const trackFAQOpen = (faq_id, category) => {
  trackEvent('faq_open', { faq_id, faq_category: category });
};

// ============== Lead / Purchase events (Ads-importable) ==============

/**
 * Fire `generate_lead` for any non-payment lead capture
 * (Contact form, Consultation form, Kundli PDF email request, etc.)
 */
export const trackGenerateLead = (formName, extra = {}) => {
  trackEvent('generate_lead', {
    form_name: formName,
    currency: 'INR',
    value: extra.value || 1,
    ...extra,
  });
};

/**
 * Razorpay `rzp.open()` fires this so we can measure how many
 * users reach the payment popup vs how many actually pay.
 */
export const trackBeginCheckout = ({ value = 99, currency = 'INR', item_name = 'Poocho Guruji', ...extra } = {}) => {
  trackEvent('begin_checkout', {
    value, currency,
    items: [{ item_name, price: value, quantity: 1 }],
    ...extra,
  });
};

/**
 * Razorpay handler success — fires GA4 `purchase` AND Google Ads conversion.
 * IMPORTANT: in GA4 → Admin → Events → mark `purchase` as Key Event,
 *           in Google Ads → Tools → Conversions → import from GA4.
 */
export const trackPurchase = ({
  transaction_id,
  value = 99,
  currency = 'INR',
  item_name = 'Poocho Guruji',
  ...extra
} = {}) => {
  trackEvent('purchase', {
    transaction_id,
    value,
    currency,
    items: [{ item_id: 'poocho_guruji_99', item_name, price: value, quantity: 1 }],
    ...extra,
  });
  // Fire Google Ads conversion (works even without GA4 import while we set that up)
  try {
    if (!isProd()) return;
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        send_to: GOOGLE_ADS_ID,
        value,
        currency,
        transaction_id,
      });
    }
  } catch (_) { /* never break the app */ }
};

export const trackPaymentFailed = ({ reason, transaction_id, value = 99 } = {}) => {
  trackEvent('payment_failed', {
    reason: (reason || 'unknown').slice(0, 80),
    transaction_id,
    value,
    currency: 'INR',
  });
};

// ============== Acharya Guruji's Feb-2026 Funnel Naming ==============
// Map old event names to the funnel naming Guruji wants in his GA4 dashboard.
// We fire BOTH names so legacy reports + new reports both populate.

/** Fires when the Poocho Guruji report finishes generating and is shown to user. */
export const trackReportGenerated = ({ session_id, tier = 'quick', value = 99, verdict, probability_pct } = {}) => {
  trackEvent('report_generated', {
    session_id,
    tier,
    value,
    currency: 'INR',
    verdict: (verdict || '').slice(0, 60),
    probability_pct,
  });
};

/** Fires when the user expands the collapsed "विस्तृत ज्योतिषीय विश्लेषण" toggle. */
export const trackTechnicalAnalysisExpanded = ({ session_id, tier = 'quick' } = {}) => {
  trackEvent('technical_analysis_expanded', { session_id, tier });
};

/** Fires when user clicks any "Upgrade to Premium / Deep Analysis देखें" CTA. */
export const trackPremiumCTAClicked = ({ source, target_tier = 'premium', current_tier = 'quick' } = {}) => {
  trackEvent('premium_cta_clicked', {
    cta_source: source,
    target_tier,
    current_tier,
  });
};

/** Alias of begin_checkout under Guruji's preferred name "payment_started". */
export const trackPaymentStarted = (params = {}) => {
  trackBeginCheckout(params);
  trackEvent('payment_started', {
    value: params.value || 99,
    currency: 'INR',
    item_name: params.item_name || 'Poocho Guruji',
    tier: params.tier,
  });
};

/** Alias of `purchase` under Guruji's preferred name "payment_success".
    Fired IN ADDITION to the canonical GA4 `purchase` event. */
export const trackPaymentSuccess = (params = {}) => {
  trackPurchase(params);
  trackEvent('payment_success', {
    transaction_id: params.transaction_id,
    value: params.value || 99,
    currency: 'INR',
    tier: params.tier,
  });
};

/** Alias of whatsapp_click under Guruji's preferred name "whatsapp_clicked". */
export const trackWhatsAppClicked = (source, extra = {}) => {
  trackWhatsAppClick(source, extra);
  trackEvent('whatsapp_clicked', { cta_source: source, ...extra });
};

/** Fires AFTER the consultation form is successfully submitted (not just CTA click). */
export const trackConsultationBooked = ({ form_name = 'consultation', value = 5100, ...extra } = {}) => {
  trackEvent('consultation_booked', {
    form_name,
    value,
    currency: 'INR',
    ...extra,
  });
  // Also fire generate_lead for Google Ads import
  trackGenerateLead(form_name, { value, ...extra });
};

// ============== Scroll-Depth Tracking ==============

let _scrollDepthBuckets = new Set();

export const initScrollDepthTracking = () => {
  if (typeof window === 'undefined') return;
  if (window.__scroll_depth_init__) return;
  window.__scroll_depth_init__ = true;

  const handler = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const winH = window.innerHeight;
    const docH = document.documentElement.scrollHeight;
    if (docH - winH < 50) return;
    const pct = Math.round(((scrollTop + winH) / docH) * 100);
    [25, 50, 75, 100].forEach((bucket) => {
      if (pct >= bucket && !_scrollDepthBuckets.has(bucket)) {
        _scrollDepthBuckets.add(bucket);
        trackEvent('scroll_depth', { depth_pct: bucket, page_path: window.location.pathname });
      }
    });
  };

  window.addEventListener('scroll', handler, { passive: true });
};

// reset depth buckets on route change
export const resetScrollDepth = () => {
  _scrollDepthBuckets = new Set();
};

export default {
  trackEvent,
  trackConsultationCTAClick,
  trackPoochoGurujiCTAClick,
  trackWhatsAppClick,
  trackCallClick,
  trackRashifalView,
  trackKundliGenerate,
  trackKundliPDFEmail,
  trackBlogRead,
  trackFAQOpen,
  trackGenerateLead,
  trackBeginCheckout,
  trackPurchase,
  trackPaymentFailed,
  trackReportGenerated,
  trackTechnicalAnalysisExpanded,
  trackPremiumCTAClicked,
  trackPaymentStarted,
  trackPaymentSuccess,
  trackWhatsAppClicked,
  trackConsultationBooked,
  captureUTM,
  getStoredUTM,
  initScrollDepthTracking,
  resetScrollDepth,
};
